import React from 'react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { chatHeaderTestIds } from '../../../common/constants/constants';
import { Divider } from '../../../common/ui/chat-header-menu/divider';

export const LIST_WIDTH_PX = {
	withStoreMessages: '280px',
	withoutStoreMessages: '215px',
} as const;

const subMenuSlidingStyles = cssMap({
	slidingViewport: {
		overflow: 'hidden',
	},
	widthWithStore: { width: '280px' },
	widthWithoutStore: { width: '215px' },
	slidingContainer: {
		display: 'flex',
		flexDirection: 'row',
		overflow: 'hidden',
		transition: 'transform 0.2s ease',
	},
	listPanel: {
		display: 'inline-flex',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.025'),
		backgroundColor: token('elevation.surface'),
		flexShrink: 0,
	},
	subMenuPanel: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		backgroundColor: token('elevation.surface'),
	},
	subMenuHeader: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: 0,
		paddingLeft: token('space.100'),
	},
	subMenuContent: {
		paddingTop: token('space.050'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
});

export interface ChatHeaderMenuSlidingPanelSection {
	items: React.ReactNode[];
}

export interface ChatHeaderMenuSlidingPanelSubMenuItem {
	key: string;
	title: string;
	content: React.ReactNode;
}

export interface ChatHeaderMenuSlidingPanelProps {
	showStoreMessages: boolean;
	openSubMenuKey: 'list' | string;
	subMenuItems: ChatHeaderMenuSlidingPanelSubMenuItem[];
	sections: ChatHeaderMenuSlidingPanelSection[];
	onBackClick: () => void;
	backButtonLabel: string;
}

export const ChatHeaderMenuSlidingPanel = ({
	showStoreMessages,
	openSubMenuKey,
	subMenuItems,
	sections,
	onBackClick,
	backButtonLabel,
}: ChatHeaderMenuSlidingPanelProps): React.JSX.Element => {
	const listWidth = showStoreMessages
		? LIST_WIDTH_PX.withStoreMessages
		: LIST_WIDTH_PX.withoutStoreMessages;

	const visibleSubMenu =
		openSubMenuKey !== 'list' ? subMenuItems.find((i) => i.key === openSubMenuKey) : undefined;
	const isSubMenuVisible = visibleSubMenu !== undefined;

	return (
		<Box
			xcss={cx(
				subMenuSlidingStyles.slidingViewport,
				showStoreMessages
					? subMenuSlidingStyles.widthWithStore
					: subMenuSlidingStyles.widthWithoutStore,
			)}
		>
			<Box
				xcss={subMenuSlidingStyles.slidingContainer}
				style={{
					width: `calc(${listWidth} * 2)`,
					transform: isSubMenuVisible ? `translateX(-${listWidth})` : 'translateX(0)',
				}}
			>
				<Box
					xcss={cx(
						subMenuSlidingStyles.listPanel,
						showStoreMessages
							? subMenuSlidingStyles.widthWithStore
							: subMenuSlidingStyles.widthWithoutStore,
					)}
					aria-hidden={isSubMenuVisible}
				>
					{sections.map(
						(section, sectionIndex) =>
							section.items.length > 0 && (
								<React.Fragment key={sectionIndex}>
									{section.items}
									{sectionIndex !== sections.length - 1 && <Divider isThin withMargin />}
								</React.Fragment>
							),
					)}
				</Box>
				<Box
					xcss={cx(
						subMenuSlidingStyles.subMenuPanel,
						showStoreMessages
							? subMenuSlidingStyles.widthWithStore
							: subMenuSlidingStyles.widthWithoutStore,
					)}
					aria-hidden={!isSubMenuVisible}
				>
					{isSubMenuVisible && visibleSubMenu && (
						<>
							<Box xcss={subMenuSlidingStyles.subMenuHeader}>
								<IconButton
									testId={chatHeaderTestIds.moreMenuBackButton}
									appearance="subtle"
									icon={ArrowLeftIcon}
									label={backButtonLabel}
									onClick={onBackClick}
								/>
								<Text as="span" weight="medium">
									{visibleSubMenu.title}
								</Text>
							</Box>
							<Box xcss={subMenuSlidingStyles.subMenuContent}>{visibleSubMenu.content}</Box>
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
};
